import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import RequiredMark from '../../../shared/RequiredMark';
import '../../../shared/StyledInput.css';

// Matches styled-input's 34px height, same styles object shape as this
// page's own AddLinkDrawer.js entity picker.
const showSelectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 4,
    borderColor: '#a9a9a9',
    boxShadow: 'none',
    fontSize: 15,
    '&:hover': { borderColor: '#a9a9a9' },
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 14 }),
};

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };

// Add Trade Show Attendance — pairs this entity with an existing Trade
// Show found via quick-search, or a brand-new one created inline (Series +
// Year + Location), always tagged with a role from the tenant's Trade Show
// Roles. Mirrors AddLinkDrawer.js's search-or-create shape.
function AddTradeShowAttendanceDrawer({ open, onOpenChange, ownerEntityId, onAdded }) {
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [newSeriesName, setNewSeriesName] = useState('');
  const [newYear, setNewYear] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setRoleId('');
    setCreatingNew(false);
    setSelectedOption(null);
    setNewSeriesName('');
    setNewYear('');
    setNewLocation('');
    setNotes('');
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    axios.get('/api/trade-show-attendee-roles', { params: { status: 'active' } })
      .then((res) => setRoles(res.data.data || []))
      .catch(() => setRoles([]));
  }, [open]);

  const loadShowOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) return callback([]);
    axios.get('/api/trade-shows/quick-search', { params: { q: inputValue } })
      .then((res) => callback((res.data.data || []).map((r) => ({ value: r.id, label: r.name }))))
      .catch(() => callback([]));
  };

  const canSave = !!roleId && (creatingNew
    ? newSeriesName.trim() && newYear.trim() && !isNaN(parseInt(newYear, 10))
    : !!selectedOption);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = { trade_show_attendee_role_id: roleId, notes: notes.trim() };
      if (creatingNew) {
        payload.series_name = newSeriesName.trim();
        payload.year = parseInt(newYear, 10);
        payload.location = newLocation.trim();
      } else {
        payload.trade_show_id = selectedOption.value;
      }
      const res = await axios.post(`/api/commercial-entities/${ownerEntityId}/trade-show-attendances`, payload);
      onAdded(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Add Trade Show Attendance"
      description="Link an existing Trade Show, or create a new one."
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={() => onOpenChange(false)} disabled={saving} />
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !canSave}
            style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: (saving || !canSave) ? 'not-allowed' : 'pointer', fontSize: 13, opacity: (saving || !canSave) ? 0.6 : 1 }}
          >
            Save
          </button>
        </div>
      )}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={fieldLabelStyle}>Role<RequiredMark /></label>
        <select
          className="styled-input"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          style={{ width: '100%', fontSize: 14 }}
        >
          <option value="">Select a role...</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={fieldLabelStyle}>
          Trade Show{' '}
          <button
            type="button"
            onClick={() => { setCreatingNew((v) => !v); setSelectedOption(null); setError(''); }}
            style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, padding: 0, textTransform: 'none', letterSpacing: 0 }}
          >
            {creatingNew ? 'Cancel' : '+ Add new Trade Show'}
          </button>
        </label>
        {creatingNew ? (
          <>
            <input
              className="styled-input"
              value={newSeriesName}
              onChange={(e) => setNewSeriesName(e.target.value)}
              placeholder="Series, e.g. Integrate"
              style={{ width: '100%', fontSize: 14, marginBottom: 8 }}
            />
            <input
              className="styled-input"
              type="number"
              value={newYear}
              onChange={(e) => setNewYear(e.target.value)}
              placeholder="Year, e.g. 2026"
              style={{ width: '100%', fontSize: 14, marginBottom: 8 }}
            />
            <input
              className="styled-input"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              placeholder="Location (optional)"
              style={{ width: '100%', fontSize: 14 }}
            />
          </>
        ) : (
          <AsyncSelect
            cacheOptions
            defaultOptions={false}
            loadOptions={loadShowOptions}
            value={selectedOption}
            onChange={setSelectedOption}
            placeholder="Search trade shows..."
            isClearable
            styles={showSelectStyles}
            noOptionsMessage={() => 'No trade shows found'}
            loadingMessage={() => 'Searching...'}
            aria-label="Trade Show"
          />
        )}
      </div>

      <div>
        <label style={fieldLabelStyle}>Notes</label>
        <textarea
          className="styled-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          style={{ width: '100%', fontSize: 14, resize: 'vertical' }}
        />
      </div>

      {error && <div style={{ color: 'red', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </FilterDrawer>
  );
}

export default AddTradeShowAttendanceDrawer;
